/**
 * useHomeDashboardFilters — the shared filter/plot state machine for the home
 * dashboards (Pulse and OEM).
 *
 * Both dashboards drive the same `ActivityMap` and the same `FilterPanel`/card
 * strip, so the filter logic is identical. This hook owns it once: the plot
 * mode, the per-layer client/server facets, the filter-panel categories +
 * selections, and every handler that mutates them. Each dashboard keeps only its
 * own `range` (time selector) and renders the chrome.
 *
 * Pulse-specific bits (the People card's `personFilter`) live here too but are
 * simply unused by the OEM dashboard — it never calls `handleSelectPerson`, so
 * `personFilter` stays null and contributes 0 to the active count.
 *
 * Web-only — do NOT import from mobile code.
 */
import {
  HomeEntityType,
  IssueType,
  useAllWorkspaceGroups,
  useCurrentWorkspace,
  useWorkspaceLabels,
} from '@gripp/shared-logic';
import { tokens } from '@gripp/shared-ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type FilterCategory } from '../components/pulseFilterPanel';

/**
 * "All" sentinel — the default option id for every filter category. A category
 * sitting on this id is inactive (not counted in the badge, not threaded to the
 * map). Mirrors the gripp-os dashboard filter convention.
 */
const ALL = 'all';

/**
 * Activity-type pill ids span two server-side axes the activity query
 * OR-combines: severity buckets (urgent/pending/solved) and issue types
 * (routine/note). Selecting any subset shows activities matching ANY of them.
 */
const SEVERITY_BUCKET_IDS = ['urgent', 'pending', 'solved'];
const ACTIVITY_ISSUE_TYPES: Record<string, IssueType | undefined> = {
  routine: IssueType.Routine,
  note: IssueType.Note,
};

export interface HomeDashboardFilters {
  // ── Raw state ──
  plotMode: HomeEntityType | null;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  filterOpen: boolean;
  setFilterOpen: (v: boolean) => void;
  personFilter: { id: string; name: string } | null;
  setPersonFilter: (p: { id: string; name: string } | null) => void;
  activityTypes: string[];

  // ── Derived: ActivityMap inputs ──
  effectivePlotMode: HomeEntityType | null;
  effectiveStatusFilter: string | null;
  isActivityLayer: boolean;
  severityBuckets: string[];
  issueTypes: IssueType[];
  groupIds: string[];
  labelIds: string[];

  // ── Derived: card strip inputs ──
  /** Single-valued status reflection for the card strip's stat-pill highlight. */
  cardStatusFilter: string | null;

  // ── Derived: filter panel inputs ──
  filterCategories: FilterCategory[];
  filterSelections: Record<string, string[]>;
  activeFilterCount: number;

  // ── Handlers ──
  handleSelectPlot: (entityType: HomeEntityType) => void;
  handleSelectStatus: (entityType: HomeEntityType, status: string) => void;
  handleSelectPerson: (person: { id: string; name: string }) => void;
  handleSelectFilter: (categoryId: string, optionId: string) => void;
  clearPlot: () => void;
  clearFilters: () => void;
}

export function useHomeDashboardFilters(): HomeDashboardFilters {
  const { t } = useTranslation();

  // plotMode: null = resting ASSET view. Card-strip selection lifts this so a
  // card-select replots the map.
  const [plotMode, setPlotMode] = useState<HomeEntityType | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Sub-filter within the active plotting mode (e.g. activity status, routine
  // name), set by clicking a chart item.
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  // Person facet — set by clicking a person in the People card (Pulse only);
  // scopes the resting field map to the assets that person has scanned.
  // Independent of plotMode (People is non-plotting); re-clicking the active
  // person clears it.
  const [personFilter, setPersonFilter] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Filter panel (header FilterBadge → half-screen panel) state.
  const [filterOpen, setFilterOpen] = useState(false);
  // Activity-type facet (multi): a subset of urgent/pending/solved (severity
  // buckets) and routine/note (issue types). When non-empty the map plots the
  // ACTIVITY layer, filtered server-side to the union (OR) of the selections.
  const [activityTypes, setActivityTypes] = useState<string[]>([]);
  // Asset-group facet (multi, real workspace groups) — narrows the asset layer.
  const [groupIds, setGroupIds] = useState<string[]>([]);
  // Activity-label facet (multi, real workspace labels) — narrows the ACTIVITY
  // layer to issues carrying any of these labels.
  const [labelIds, setLabelIds] = useState<string[]>([]);

  // Real options for the Groups / Labels sections. Both gate on the workspace id.
  const workspace = useCurrentWorkspace();
  const { groups } = useAllWorkspaceGroups(workspace?.id);
  const { labels } = useWorkspaceLabels(workspace?.id);

  // ── Effective map layer ──
  // The activity layer is "active" when an activity-TYPE facet (severity / type
  // pill) is set. LABELS are NOT here: matching gripp-os, a label narrows the
  // ASSET fleet rather than switching the map to activity points. A concrete
  // non-activity card selection (Asset/Routine/Product) takes precedence;
  // otherwise the activity-type facet decides activity-vs-resting.
  const activityLayerActive = activityTypes.length > 0;
  const effectivePlotMode: HomeEntityType | null =
    plotMode && plotMode !== HomeEntityType.Activity
      ? plotMode
      : activityLayerActive
      ? HomeEntityType.Activity
      : plotMode;
  const isActivityLayer = effectivePlotMode === HomeEntityType.Activity;

  // Activity-type pill ids split across the two server-side axes the activity
  // query OR-combines: severity buckets and issue types.
  const severityBuckets = activityTypes.filter((id) =>
    SEVERITY_BUCKET_IDS.includes(id)
  );
  const issueTypes = activityTypes
    .map((id) => ACTIVITY_ISSUE_TYPES[id])
    .filter((it): it is IssueType => !!it);

  // statusFilter is the single client-side sub-filter for the NON-activity
  // layers. The activity layer filters server-side, so suppress it there.
  const effectiveStatusFilter = isActivityLayer ? null : statusFilter;

  // The Activity card's stat-pill highlight is single-valued; reflect it only
  // when exactly one severity bucket is selected (the panel owns the full multi
  // state). Other layers pass their real client-side sub-filter.
  const cardStatusFilter = isActivityLayer
    ? activityTypes.length === 1
      ? activityTypes[0]
      : null
    : effectiveStatusFilter;

  const toggle = (arr: string[], id: string): string[] =>
    arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];

  // ── Card-strip handlers ──
  // Toggle a card's plotting mode: selecting the active one again clears it.
  const handleSelectPlot = (entityType: HomeEntityType) => {
    setStatusFilter(null);
    if (entityType === HomeEntityType.Activity) {
      // Activity card toggles the activity layer (incl. its panel filters).
      if (isActivityLayer) {
        setPlotMode(null);
        setActivityTypes([]);
        setLabelIds([]);
      } else {
        setPlotMode(HomeEntityType.Activity);
      }
      return;
    }
    // Asset/Routine/Product layer — clear the activity-only facets and toggle.
    setActivityTypes([]);
    setLabelIds([]);
    setPlotMode((current) => (current === entityType ? null : entityType));
  };

  const clearPlot = () => {
    setPlotMode(null);
    setStatusFilter(null);
    setActivityTypes([]);
    setLabelIds([]);
  };

  // Click a chart item (stat pill / routine row). For the Activity card this
  // toggles the severity bucket in the multi activity-type facet (shared with
  // the panel); for the other layers it sets the single client-side sub-filter.
  const handleSelectStatus = (entityType: HomeEntityType, status: string) => {
    if (entityType === HomeEntityType.Activity) {
      // Drop a conflicting non-activity plot mode so the activity layer shows,
      // and clear its chart sub-filter. The activity layer ignores statusFilter,
      // so a stale value would silently filter the resting ASSET map once the
      // user later clears the activity types (back to resting).
      if (plotMode && plotMode !== HomeEntityType.Activity) setPlotMode(null);
      setStatusFilter(null);
      setActivityTypes((current) => toggle(current, status));
      return;
    }
    setPlotMode(entityType);
    setStatusFilter((current) =>
      plotMode === entityType && current === status ? null : status
    );
  };

  // Toggle the person facet from the People card: re-selecting the active
  // person clears it. Scopes the resting field map to that person's assets.
  const handleSelectPerson = (person: { id: string; name: string }) => {
    setPersonFilter((current) => (current?.id === person.id ? null : person));
  };

  // Three MULTI-select sections. Activity types carry colour dots (the canonical
  // Crop Circle severity/type swatches); groups and labels are plain text pills
  // populated from real workspace data. Each section's first option is "All".
  const filterCategories = useMemo<FilterCategory[]>(() => {
    const all = { id: ALL, label: t('home.filters.all', 'All') };
    return [
      {
        id: 'activityTypes',
        label: t('home.filters.activityTypes', 'ACTIVITY TYPES'),
        options: [
          all,
          {
            id: 'urgent',
            label: t('home.activity.urgent', 'Urgent'),
            dotColor: tokens.colors.semantic.error,
          },
          {
            id: 'pending',
            label: t('home.activity.pending', 'Pending'),
            dotColor: tokens.colors.semantic.warning,
          },
          {
            id: 'solved',
            label: t('home.activity.solved', 'Solved'),
            dotColor: tokens.colors.semantic.success,
          },
          {
            id: 'routine',
            label: t('home.activity.routine', 'Routine'),
            dotColor: tokens.colors.semantic.info,
          },
          {
            id: 'note',
            label: t('home.activity.note', 'Note'),
            dotColor: tokens.colors.label.violet,
          },
        ],
      },
      {
        id: 'groups',
        label: t('home.filters.groups', 'GROUPS'),
        options: [all, ...groups.map((g) => ({ id: g.id, label: g.name }))],
      },
      {
        id: 'labels',
        label: t('home.filters.labels', 'LABELS'),
        options: [
          all,
          ...labels.map((l: { id: string; name: string }) => ({
            id: l.id,
            label: l.name,
          })),
        ],
      },
    ];
  }, [t, groups, labels]);

  // Current selections per category (multi). Empty array = the "All" default.
  const filterSelections = useMemo<Record<string, string[]>>(
    () => ({
      activityTypes,
      groups: groupIds,
      labels: labelIds,
    }),
    [activityTypes, groupIds, labelIds]
  );

  // Toggle a pill in/out of its category. The "All" pill clears the category.
  const handleSelectFilter = (categoryId: string, optionId: string) => {
    if (categoryId === 'activityTypes') {
      if (optionId === ALL) {
        setActivityTypes([]);
        return;
      }
      // Drop a conflicting non-activity plot mode so the activity layer shows,
      // and clear its stale chart sub-filter (see handleSelectStatus) so it
      // can't re-apply to the resting ASSET map when activity types are cleared.
      if (plotMode && plotMode !== HomeEntityType.Activity) setPlotMode(null);
      setStatusFilter(null);
      setActivityTypes((current) => toggle(current, optionId));
      return;
    }
    if (categoryId === 'groups') {
      if (optionId === ALL) {
        setGroupIds([]);
        return;
      }
      setGroupIds((current) => toggle(current, optionId));
      return;
    }
    if (categoryId === 'labels') {
      if (optionId === ALL) {
        setLabelIds([]);
        return;
      }
      // Labels narrow the ASSET fleet (gripp-os model). Drop any active plot mode
      // so the map returns to the resting ASSET view, where the label filter
      // takes effect (the label flows to the asset query, not an activity layer).
      // Clear the chart sub-filter too, or it would silently filter that resting
      // ASSET map with a now-orphaned status value.
      if (plotMode) setPlotMode(null);
      setStatusFilter(null);
      setLabelIds((current) => toggle(current, optionId));
      return;
    }
  };

  // Header badge / panel title count = total active (non-"All") selections plus
  // the person facet (set by the People card).
  const activeFilterCount =
    activityTypes.length +
    groupIds.length +
    labelIds.length +
    (personFilter ? 1 : 0);

  // Reset clears every panel facet AND the person facet, returning the map to
  // the resting asset view.
  const clearFilters = () => {
    setPlotMode(null);
    setStatusFilter(null);
    setActivityTypes([]);
    setGroupIds([]);
    setLabelIds([]);
    setPersonFilter(null);
  };

  return {
    plotMode,
    searchQuery,
    setSearchQuery,
    filterOpen,
    setFilterOpen,
    personFilter,
    setPersonFilter,
    activityTypes,
    effectivePlotMode,
    effectiveStatusFilter,
    isActivityLayer,
    severityBuckets,
    issueTypes,
    groupIds,
    labelIds,
    cardStatusFilter,
    filterCategories,
    filterSelections,
    activeFilterCount,
    handleSelectPlot,
    handleSelectStatus,
    handleSelectPerson,
    handleSelectFilter,
    clearPlot,
    clearFilters,
  };
}
